const MISSING_VALUE = String.fromCharCode(0x2014);

const FUT_ROOTS = new Set([
    'ES', 'MES', 'NQ', 'MNQ', 'RTY', 'M2K', 'YM', 'MYM', 'CL', 'GC', 'SI', 'HG', 'NG', 'ZB', 'ZN', 'ZF', 'ZT', '6A', '6B', '6C', '6E', '6J', '6N', '6S',
]);

const CRYPTO_ROOTS = new Set(['BTC', 'ETH', 'LTC', 'XRP', 'DOGE', 'SOL', 'ADA', 'BNB']);

const FX_CODES = new Set(['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'NZD', 'SEK', 'NOK', 'DKK', 'HKD', 'SGD', 'CNH', 'CNY', 'MXN', 'ZAR', 'TRY', 'PLN', 'HUF', 'CZK', 'ILS', 'AED', 'SAR']);

export function sanitizeSymbolForChart(raw: unknown): string {
    const s = String(raw ?? '').trim();
    if (!s) return '';
    if (s === MISSING_VALUE) return '';

    const withoutExchange = s.includes(':') ? (s.split(':').pop() || s) : s;
    const upper = withoutExchange.toUpperCase().trim().replace(/^\//, '');

    const cleaned = upper.replace(/!$/g, '');
    const cont = cleaned.match(/^([A-Z0-9]{1,4})\d{1,2}$/);
    if (cont && FUT_ROOTS.has(cont[1])) return cont[1];

    const futContract = cleaned.match(/^([A-Z0-9]{1,4})[FGHJKMNQUVXZ]\d{1,4}$/);
    if (futContract && FUT_ROOTS.has(futContract[1])) return futContract[1];

    if (!cleaned || cleaned === MISSING_VALUE) return '';
    return cleaned;
}

export function contractSymbolToYyyymm(raw: unknown): string | null {
    const s = String(raw || '').trim().toUpperCase().replace(/^\/+/, '');
    const m = /^([A-Z0-9]{1,6})([FGHJKMNQUVXZ])(\d{1,2}|\d{4})$/.exec(s);
    if (!m) return null;

    const monthMap: Record<string, string> = { F: '01', G: '02', H: '03', J: '04', K: '05', M: '06', N: '07', Q: '08', U: '09', V: '10', X: '11', Z: '12' };
    const month = monthMap[m[2]];
    if (!month) return null;

    const yearPart = m[3];
    let yearNum: number;
    if (yearPart.length === 4) {
        yearNum = Number(yearPart);
    } else if (yearPart.length === 2) {
        yearNum = 2000 + Number(yearPart);
    } else {
        const d = Number(yearPart);
        const now = new Date();
        const currentYear = now.getFullYear();
        const base = Math.floor(currentYear / 10) * 10;
        let cand = base + d;
        if (cand < currentYear - 5) cand += 10;
        if (cand > currentYear + 5) cand -= 10;
        yearNum = cand;
    }

    if (!Number.isFinite(yearNum) || yearNum < 1970) return null;
    return `${String(yearNum)}${month}`;
}

export function inferInstrumentType(symbol: string): 'stock' | 'future' | 'forex' | 'crypto' {
    const s = sanitizeSymbolForChart(symbol);
    if (!s) return 'stock';

    if (CRYPTO_ROOTS.has(s) || /^(BTC|ETH|LTC|XRP|DOGE|SOL|ADA|BNB)(USD)?$/.test(s)) return 'crypto';
    if (/^[A-Z]{3}[./-][A-Z]{3}$/.test(s)) {
        const parts = s.split(/[./-]/);
        const base = parts[0];
        const quote = parts[1];
        if (CRYPTO_ROOTS.has(base) || CRYPTO_ROOTS.has(quote)) return 'crypto';
        if (FX_CODES.has(base) && FX_CODES.has(quote)) return 'forex';
    }
    if (/^[A-Z]{6}$/.test(s)) {
        const base = s.slice(0, 3);
        const quote = s.slice(3, 6);
        if (CRYPTO_ROOTS.has(base) || CRYPTO_ROOTS.has(quote)) return 'crypto';
        if (FX_CODES.has(base) && FX_CODES.has(quote)) return 'forex';
    }

    const looksLikeContract = /^([A-Z0-9]{1,6})([FGHJKMNQUVXZ])(\d{1,2}|\d{4})$/.test(s);
    if (FUT_ROOTS.has(s) || looksLikeContract) return 'future';

    return 'stock';
}
